import { createTheme, alpha } from "@mui/material";
import type { PaletteMode } from "@mui/material";
import type { ThemeOptions, Shadows } from "@mui/material/styles";

// ----------------------------------------------------------------------
// Type Augmentation — CONTRACT PRESERVED. `neutral` is permanent.
// ----------------------------------------------------------------------
declare module "@mui/material/styles" {
  interface Palette {
    /** Ledger surface ladder beyond MUI's default/paper pair. `well` sits
     *  BELOW the canvas (code blocks, recessed panels, footer); `elevated`
     *  and `overlay` are the menu and dialog steps. */
    surfaces: {
      well: string;
      elevated: string;
      overlay: string;
    };
    neutral: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
  }
  interface PaletteOptions {
    surfaces?: {
      well: string;
      elevated: string;
      overlay: string;
    };
    neutral?: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
  }
}

// ----------------------------------------------------------------------
// Ledger constants (import these instead of hardcoding)
// ----------------------------------------------------------------------

/**
 * Obsidian nameplate. The app bar is this color in DARK mode (and the
 * HeroBand / footer logo tile / mobile drawer stay obsidian in both modes).
 * In LIGHT mode the Header now inverts to the *_LIGHT paper set below so the
 * masthead adapts with the theme instead of staying a black bar.
 */
export const MASTHEAD_BG = "#0B0B0C";
export const MASTHEAD_FG = "#F5F5F5";
export const MASTHEAD_MUTED = "rgba(255,255,255,0.64)";
export const MASTHEAD_RULE = "rgba(255,255,255,0.08)";

/** Light-mode masthead: clean paper white + ink, harmonized with bg #F7F5F2. */
export const MASTHEAD_BG_LIGHT = "#FFFFFF";
export const MASTHEAD_FG_LIGHT = "#0B0B0C";
export const MASTHEAD_MUTED_LIGHT = "rgba(11,11,12,0.58)";
export const MASTHEAD_RULE_LIGHT = "rgba(11,11,12,0.10)";

/** Header is position:sticky at this height. Sticky toolbars offset by it. */
export const HEADER_HEIGHT = { xs: 64, md: 72 } as const;

export const FONT_SANS =
  "'Schibsted Grotesk Variable', -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
export const FONT_MONO =
  "'JetBrains Mono Variable', ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', monospace";

const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)";
const EASE_DRAWER = "cubic-bezier(0.32, 0.72, 0, 1)";

/**
 * Print-grain noise (SVG feTurbulence data URI). Layered at 2-3% opacity over
 * hero glows to kill gradient banding and add paper texture — the standard
 * "life without slop" move (Raycast/grainy-gradients). Never on content.
 */
export const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`;

// True-gray obsidian neutral scale. FIXED order (50 light -> 900 dark) in both
// modes — same ordering as the old slate scale, so existing neutral[*] usages
// keep their meaning; only the hue changed (de-blued).
const neutral = {
  50: "#F6F6F6",
  100: "#EFEFEF",
  200: "#E3E3E3",
  300: "#C9C9C9",
  400: "#A3A3A6",
  500: "#7A7A7E",
  600: "#5B5B5E",
  700: "#3F3F42",
  800: "#232326",
  900: "#121214",
};

export const getThemeOptions = (mode: PaletteMode): ThemeOptions => {
  const dark = mode === "dark";

  // Ink — the interface is monochrome first. primary = ink so existing
  // color="primary" usages degrade to quiet monochrome, not accent spray.
  // Dark ink is a near-white with a hint of cool (never #FFF — pure white is
  // reserved for the hover/brightest interactive state, Resend/Linear rule).
  const ink = dark ? "#F0F1F2" : "#191817";
  const inkHover = dark ? "#FFFFFF" : "#000000";

  // Ember — the molten seam under the glass. Links, focus, active marks,
  // editor's picks, and the rare high-emphasis CTA. Never a background wash.
  const ember = dark
    ? {
        lighter: "#3A1D12",
        light: "#FF8F66",
        main: "#FF7849",
        dark: "#F0663B",
        darker: "#D9552E",
        contrastText: "#181818",
      }
    : {
        lighter: "#F6DED3",
        light: "#D06A45",
        main: "#B33A0F",
        dark: "#99310C",
        darker: "#822A0A",
        contrastText: "#FFFFFF",
      };

  const primaryCol = {
    lighter: dark ? "#232326" : "#E3E3E3",
    light: dark ? "#C9C9C9" : "#3F3F42",
    main: ink,
    dark: inkHover, // contained-hover slot
    darker: inkHover,
    contrastText: dark ? "#0B0B0C" : "#FFFFFF",
  };

  const successCol = dark
    ? {
        lighter: "#12291D",
        light: "#6ED4A6",
        main: "#45C48A",
        dark: "#2FA36E",
        darker: "#1F7A50",
        contrastText: "#0B0B0C",
      }
    : {
        lighter: "#D8EDE2",
        light: "#3FA372",
        main: "#197C4D",
        dark: "#135E3B",
        darker: "#0E4A2E",
        contrastText: "#FFFFFF",
      };

  const errorCol = dark
    ? {
        lighter: "#33161C",
        light: "#FF9AA8",
        main: "#FF7285",
        dark: "#E05468",
        darker: "#C13E51",
        contrastText: "#0B0B0C",
      }
    : {
        lighter: "#F5DCE1",
        light: "#D45C74",
        main: "#BB2E4C",
        dark: "#9A2540",
        darker: "#7C1D33",
        contrastText: "#FFFFFF",
      };

  const warningCol = dark
    ? {
        lighter: "#33290F",
        light: "#F5D076",
        main: "#EFBE4A",
        dark: "#D3A32E",
        darker: "#B0871F",
        contrastText: "#0B0B0C",
      }
    : {
        lighter: "#F0E7CF",
        light: "#B08A1E",
        main: "#8A6100",
        dark: "#6E4D00",
        darker: "#553B00",
        contrastText: "#FFFFFF",
      };

  const infoCol = dark
    ? {
        lighter: "#16232E",
        light: "#A5C8E3",
        main: "#82B1D6",
        dark: "#648FB5",
        darker: "#4C7295",
        contrastText: "#0B0B0C",
      }
    : {
        lighter: "#DCE6EE",
        light: "#5E88A8",
        main: "#38678C",
        dark: "#2B5271",
        darker: "#204058",
        contrastText: "#FFFFFF",
      };

  // Dark-mode surface ladder — tightened to Linear-spacing (steps of ~3 L*)
  // so depth reads as machined tone, not islands. Page bg stays true obsidian.
  // WELL sits BELOW the canvas (code blocks, recessed panels, footer) — the
  // cheapest depth cue on dark (Resend pattern). Light mode: warm paper page
  // (FT/Semafor direction, kept quiet) with pure white cards.
  const bgDefault = dark ? "#0B0B0C" : "#F7F5F2";
  const well = dark ? "#070708" : "#F1EEE9"; // recessed: below the canvas
  const paper = dark ? "#101013" : "#FFFFFF";
  const elevated = dark ? "#17171B" : "#FFFFFF"; // menus, drawers, autocomplete
  const overlay = dark ? "#1B1B20" : "#FFFFFF"; // dialogs, the post modal
  const hoverSurface = dark ? "#15151A" : "#F0EEEA";
  // Border ladder as ink-keyed alpha so hairlines sit correctly on every
  // ladder step and hover gets a free "strong" tier (Vercel/Raycast pattern).
  const divider = dark ? "rgba(255,255,255,0.08)" : "rgba(24,23,22,0.10)";
  const dividerStrong = dark ? "rgba(255,255,255,0.16)" : "rgba(24,23,22,0.20)";
  // One tier brighter than the old #A1A1A6 — muted gray was carrying whole
  // card bodies and read dim-on-dim (brief R7: body copy sits above muted).
  const textSecondary = dark ? "#A8A9AE" : "#5B5955";
  const textDisabled = dark ? "#6A6A6F" : "#A3A19D";

  // Card edges live on an alpha keyed to each mode's ink, so a hairline border
  // harmonizes with any surface it sits on and brightens on hover (Vercel/Geist
  // pattern). Depth = this hairline + (light only) a soft shadow; dark = tone.
  const cardBorder = dark ? "rgba(255,255,255,0.10)" : "rgba(24,23,22,0.10)";
  const cardBorderHover = dark ? "rgba(255,255,255,0.18)" : "rgba(24,23,22,0.17)";
  // "Lit from above" — a one-pixel top-edge highlight plus a faint top sheen
  // is what makes dark cards read machined instead of flat (Linear/Vercel).
  const cardSheen = dark
    ? "linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0) 36%)"
    : "none";
  const cardTopEdge = dark ? "inset 0 1px 0 rgba(255,255,255,0.06)" : "none";
  // Ember focal treatment for the single featured element per view.
  const emberBorder = dark ? "rgba(255,120,73,0.50)" : "rgba(179,58,15,0.45)";
  const emberGlowRest = dark
    ? "0 0 0 1px rgba(255,120,73,0.22)"
    : "0 8px 24px rgba(179,58,15,0.14), 0 1px 2px rgba(11,11,12,0.06)";
  const emberGlowHover = dark
    ? "0 0 0 1px rgba(255,120,73,0.34)"
    : "0 12px 30px rgba(179,58,15,0.19), 0 1px 2px rgba(11,11,12,0.06)";

  // Border-first elevation. Light mode: three dual-layer levels, each a tight
  // "key" shadow (defines the edge) + a soft "ambient" shadow (implies
  // distance), tinted with the ink #0B0B0C instead of pure black so depth
  // reads as this brand's, not generic Bootstrap soup (Fluent 2 / Stripe).
  // Dark mode: ALL none — elevation is a lighter surface + 1px border.
  const shadows = Array(25).fill("none") as Shadows;
  if (!dark) {
    const restShadow = "0 1px 2px rgba(11,11,12,0.05), 0 1px 1px rgba(11,11,12,0.03)";
    const popShadow = "0 1px 2px rgba(11,11,12,0.06), 0 6px 16px rgba(11,11,12,0.10)";
    const dialogShadow = "0 2px 4px rgba(11,11,12,0.08), 0 16px 40px rgba(11,11,12,0.14)";
    for (let i = 1; i <= 3; i += 1) shadows[i] = restShadow;
    for (let i = 4; i <= 7; i += 1) shadows[i] = popShadow;
    for (let i = 8; i <= 24; i += 1) shadows[i] = dialogShadow;
  }

  return {
    palette: {
      mode,
      common: { black: "#000000", white: "#FFFFFF" },
      primary: primaryCol,
      secondary: ember,
      success: successCol,
      error: errorCol,
      warning: warningCol,
      info: infoCol,
      background: { default: bgDefault, paper },
      surfaces: { well, elevated, overlay },
      text: { primary: ink, secondary: textSecondary, disabled: textDisabled },
      divider,
      action: {
        active: textSecondary,
        hover: alpha(dark ? "#FFFFFF" : "#000000", dark ? 0.05 : 0.04),
        selected: alpha(dark ? "#FFFFFF" : "#000000", dark ? 0.08 : 0.06),
        focus: alpha(ember.main, 0.16),
        disabled: alpha(ink, 0.32),
        disabledBackground: alpha(ink, 0.1),
      },
      neutral,
    },

    shape: { borderRadius: 6 },
    spacing: 8,
    shadows,

    transitions: {
      duration: {
        shortest: 100,
        shorter: 150,
        short: 180,
        standard: 220,
        complex: 280,
        enteringScreen: 200,
        leavingScreen: 160,
      },
      easing: {
        easeOut: EASE_OUT,
        easeInOut: EASE_DRAWER, // drawers only
        easeIn: EASE_OUT, // never ease-in; alias to easeOut defensively
        sharp: EASE_OUT,
      },
    },

    typography: {
      fontFamily: FONT_SANS,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600, // nothing above 650 anywhere
      h1: {
        // Display scale — AAA display type is big, weight-capped, and
        // aggressively negative-tracked (Linear 80px/600/-3px; Vercel
        // 48px/600/-2.4px). Ceiling kept at 3.75rem so a 2-line hero title
        // still fits a 1280px viewport without wrapping to three lines.
        fontSize: "clamp(2.25rem, 1.4rem + 3vw, 3.75rem)",
        fontWeight: 650,
        lineHeight: 1.05,
        letterSpacing: "-0.035em",
      },
      h2: {
        fontSize: "clamp(1.5rem, 1.2rem + 1.2vw, 2rem)",
        fontWeight: 650,
        lineHeight: 1.15,
        letterSpacing: "-0.02em",
      },
      h3: { fontSize: "1.375rem", fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.01em" },
      h4: { fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.35, letterSpacing: "-0.005em" },
      h5: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.45, letterSpacing: 0 },
      h6: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.5, letterSpacing: 0 },
      subtitle1: { fontSize: "1rem", fontWeight: 500, lineHeight: 1.5 },
      subtitle2: { fontSize: "0.875rem", fontWeight: 500, lineHeight: 1.5 },
      body1: { fontSize: "1rem", fontWeight: 400, lineHeight: 1.65 },
      body2: { fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.6 },
      button: { fontSize: "0.875rem", fontWeight: 500, textTransform: "none", letterSpacing: 0 },
      // caption = the metadata voice: dates, sources, read times. Always mono.
      caption: {
        fontFamily: FONT_MONO,
        fontSize: "0.75rem",
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: 0,
        fontVariantNumeric: "tabular-nums",
      },
      // overline = kicker/group label. Mono, SENTENCE CASE — never tracked caps.
      overline: {
        fontFamily: FONT_MONO,
        fontSize: "0.75rem",
        fontWeight: 500,
        lineHeight: 1.5,
        letterSpacing: "0.02em",
        textTransform: "none",
        fontVariantNumeric: "tabular-nums",
      },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": { colorScheme: dark ? "dark" : "light" },
          html: { scrollBehavior: "auto" }, // kill global smooth-scroll (SPA nav lag)
          "::selection": { backgroundColor: alpha(ember.main, 0.25) },
          "*:focus-visible": { outline: `2px solid ${ember.main}`, outlineOffset: 2 },
          "code, kbd, samp, pre": { fontFamily: FONT_MONO },
          time: { fontVariantNumeric: "tabular-nums" },
          "*::-webkit-scrollbar": { width: 10, height: 10 },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: dark ? "#2E2E33" : "#C9C9C9",
            borderRadius: 8,
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
          "@media (prefers-reduced-motion: reduce)": {
            "*, *::before, *::after": {
              animationDuration: "0.01ms !important",
              animationIterationCount: "1 !important",
              transitionDuration: "0.01ms !important",
              scrollBehavior: "auto !important",
            },
          },
        },
      },

      MuiButtonBase: { defaultProps: { disableRipple: true } },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 6,
            minHeight: 36,
            paddingInline: 14,
            backgroundImage: "none",
            transition: `background-color 150ms ${EASE_OUT}, border-color 150ms ${EASE_OUT}, color 150ms ${EASE_OUT}, transform 120ms ${EASE_OUT}`,
            "&:active": { transform: "scale(0.98)" },
            "&:focus-visible": { outline: `2px solid ${ember.main}`, outlineOffset: 2 },
          },
          containedPrimary: {
            "&:hover": { backgroundColor: inkHover },
          },
          containedSecondary: {
            "&:hover": { backgroundColor: ember.dark },
          },
          outlined: {
            borderColor: divider,
            color: ink,
            "&:hover": { borderColor: dividerStrong, backgroundColor: hoverSurface },
          },
          text: {
            color: textSecondary,
            "&:hover": { color: ink, backgroundColor: hoverSurface },
          },
          sizeSmall: { minHeight: 30, fontSize: "0.8125rem", paddingInline: 10 },
          sizeLarge: { minHeight: 42, fontSize: "0.9375rem", paddingInline: 18 },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            transition: `background-color 150ms ${EASE_OUT}, color 150ms ${EASE_OUT}`,
          },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 10,
            border: `1px solid ${cardBorder}`,
            backgroundColor: paper,
            // Dark cards are "lit from above": faint top sheen + 1px top-edge
            // highlight. Light keeps the dual-layer resting shadow instead.
            backgroundImage: cardSheen,
            boxShadow: dark ? cardTopEdge : shadows[1],
            transition: `border-color 160ms ${EASE_OUT}, background-color 160ms ${EASE_OUT}, box-shadow 160ms ${EASE_OUT}`,
            // The canonical hover (no positional jumps): surface steps one
            // ladder value up + border brightens one alpha tier; light mode
            // adds the hover shadow. Images inside un-dim via this selector.
            '&[data-interactive="true"]': {
              cursor: "pointer",
              "&:hover": {
                borderColor: cardBorderHover,
                backgroundColor: dark ? hoverSurface : paper,
                boxShadow: dark ? cardTopEdge : shadows[4],
              },
              "&:active": {
                backgroundColor: dark ? paper : hoverSurface,
              },
            },
            // The single featured element per view: larger radius, ember edge,
            // and one rationed ember glow. Ration to ONE element per section.
            '&[data-variant="featured"]': {
              borderRadius: 14,
              borderColor: emberBorder,
              boxShadow: dark ? `${emberGlowRest}, ${cardTopEdge}` : emberGlowRest,
              '&[data-interactive="true"]:hover': {
                borderColor: emberBorder,
                boxShadow: dark ? `${emberGlowHover}, ${cardTopEdge}` : emberGlowHover,
              },
            },
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          outlined: { borderColor: divider },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: "default" },
        styleOverrides: {
          // Color, border, and scroll-elevation are driven by the Header
          // component per palette.mode + scroll state (so light mode gets a
          // paper masthead, dark keeps obsidian). The theme only neutralizes
          // MUI's defaults here.
          root: {
            backgroundColor: "transparent",
            color: "inherit",
            backgroundImage: "none",
            boxShadow: "none",
            backdropFilter: "none",
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 4, // squared mono tag — part of the ledger signature
            height: 22,
            fontFamily: FONT_MONO,
            fontSize: "0.75rem",
            fontWeight: 400,
            letterSpacing: 0,
          },
          label: { paddingInline: 8 },
          outlined: { borderColor: divider, color: textSecondary },
          filled: { backgroundColor: hoverSurface, color: textSecondary },
          colorSecondary: {
            color: ember.main,
            borderColor: alpha(ember.main, 0.4),
            backgroundColor: "transparent",
          },
        },
      },

      MuiTextField: { defaultProps: { size: "small" } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            backgroundColor: paper,
            transition: `box-shadow 150ms ${EASE_OUT}`,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: divider },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: dividerStrong },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: ember.main,
              borderWidth: 1,
            },
            "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(ember.main, 0.18)}` },
          },
          input: {
            fontSize: "0.9375rem",
            "&::placeholder": { color: textSecondary, opacity: 1 },
          },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { fontSize: "0.875rem" } } },
      MuiFormHelperText: { styleOverrides: { root: { fontSize: "0.75rem", marginLeft: 2 } } },

      MuiLink: {
        defaultProps: { underline: "always" },
        styleOverrides: {
          root: {
            color: ember.main,
            textUnderlineOffset: "3px",
            textDecorationColor: alpha(ember.main, 0.4),
            transition: `text-decoration-color 150ms ${EASE_OUT}, color 150ms ${EASE_OUT}`,
            "&:hover": { textDecorationColor: ember.main },
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: { height: 2, backgroundColor: ember.main },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 40,
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            color: textSecondary,
            "&.Mui-selected": { color: ink },
          },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 6,
            borderColor: divider,
            color: textSecondary,
            "&.Mui-selected": { color: ink, backgroundColor: hoverSurface },
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            border: `1px solid ${divider}`,
            backgroundColor: elevated,
            boxShadow: shadows[4],
            marginTop: 4,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: { root: { fontSize: "0.875rem", minHeight: 36 } },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            border: `1px solid ${divider}`,
            backgroundColor: elevated,
            boxShadow: shadows[4],
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            border: `1px solid ${divider}`,
            backgroundColor: overlay,
            backgroundImage: "none",
            boxShadow: shadows[8],
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(5,5,6,0.55)",
            backdropFilter: "none",
            "&.MuiBackdrop-invisible": { backgroundColor: "transparent" },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundImage: "none" },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily: FONT_MONO,
            fontSize: "0.75rem",
            borderRadius: 4,
            backgroundColor: dark ? "#2A2A2F" : "#1F1F23",
            color: "#F5F5F5",
          },
        },
      },

      MuiDivider: { styleOverrides: { root: { borderColor: divider } } },

      // Ratings ride the ember ramp — the default MUI gold is an off-palette
      // second accent on a single-ember system.
      MuiRating: {
        styleOverrides: {
          iconFilled: { color: ember.main },
          iconHover: { color: ember.light },
          iconEmpty: { color: dividerStrong },
        },
      },

      MuiSkeleton: {
        defaultProps: { animation: false }, // static placeholders — no shimmer
        styleOverrides: { root: { backgroundColor: hoverSurface, borderRadius: 6 } },
      },
    },
  };
};

export const getTheme = (mode: PaletteMode) => createTheme(getThemeOptions(mode));
